/**
 * Theme — Light / Dark / System, per user.
 *
 * The SERVER is the source of truth (users.theme via PATCH /auth/me) so a
 * choice follows the user between machines. localStorage holds a mirror
 * purely so the pre-paint script in index.html can apply the right class
 * before React mounts — no white flash for dark users, no dark flash for
 * light ones. Default is Light: nothing changes for anyone who never opts in.
 */

export type Theme = "light" | "dark" | "system";

export const THEMES: Theme[] = ["light", "dark", "system"];

/** Must match the key read by the inline script in index.html. */
export const THEME_STORAGE_KEY = "rp.theme";

export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

export function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : "light";
  } catch {
    return "light";
  }
}

export function storeTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode / blocked storage — the server copy still wins */
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Resolve "system" to what the OS is asking for right now. */
export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light";
  return theme;
}

/** Flip the `dark` class on <html>. Idempotent. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveTheme(theme) === "dark");
}

/** While the user is on "system", follow OS changes live. Returns an
 *  unsubscribe; a no-op for explicit light/dark. */
export function watchSystemTheme(theme: Theme): () => void {
  if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) {
    return () => {};
  }
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => applyTheme("system");
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
